import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField
} from '@mui/material';
import {
  CheckCircle,
  Schedule,
  Download,
  Description
} from '@mui/icons-material';
import axios from 'axios';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ComplianceDashboard = () => {
  const [companyId, setCompanyId] = useState(null);
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processDialog, setProcessDialog] = useState({ open: false, steps: [] });
  const [currentFiling, setCurrentFiling] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  
  // Separate state for each filing type
  // PF State
  const [pfSelectedMonth, setPfSelectedMonth] = useState('');
  const [pfSelectedYear, setPfSelectedYear] = useState('');
  
  // PT State
  const [ptSelectedMonth, setPtSelectedMonth] = useState('');
  const [ptSelectedYear, setPtSelectedYear] = useState('');
  
  // ESI State
  const [esiSelectedMonth, setEsiSelectedMonth] = useState('');
  const [esiSelectedYear, setEsiSelectedYear] = useState('');
  
  // TDS State
  const [tdsSelectedQuarter, setTdsSelectedQuarter] = useState('');
  const [tdsSelectedYear, setTdsSelectedYear] = useState('');

  // PF Form 10 State
  const [form10FromDate, setForm10FromDate] = useState('');
  const [form10ToDate, setForm10ToDate] = useState('');

  // Loading states for individual filing types
  const [pfLoading, setPfLoading] = useState(false);
  const [ptLoading, setPtLoading] = useState(false);
  const [esiLoading, setEsiLoading] = useState(false);
  const [tdsLoading, setTdsLoading] = useState(false);
  const [form10Loading, setForm10Loading] = useState(false);



  const [lwfSelectedHalf, setLwfSelectedHalf] = useState('');
const [lwfSelectedYear, setLwfSelectedYear] = useState('');
const [lwfLoading, setLwfLoading] = useState(false);

  // Function to get month name from month number
  const getMonthName = (monthNumber) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[monthNumber - 1] || 'Invalid Month';
  };
const halfYears = [
  { value: 'Jan-Jun', label: 'January - June (First Half)', months: ['January', 'February', 'March', 'April', 'May', 'June'] },
  { value: 'Jul-Dec', label: 'July - December (Second Half)', months: ['July', 'August', 'September', 'October', 'November', 'December'] }
];

  const formatDateInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getMonthStartDate = (date) => {
    const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
    return formatDateInput(monthStart);
  };

  const isPFForm10Filing = (filing) => {
    const reference = filing?.filing_reference || '';
    const generatedFile = filing?.generated_file || '';
    const period = filing?.period_month || '';
    return reference.startsWith('PF10-') || generatedFile.includes('PF_Form_10') || period.startsWith('Form 10');
  };

  const getFilingDisplayType = (filing) => isPFForm10Filing(filing) ? 'PF Form 10' : filing.filing_type;

  // Function to get quarter name
  const getQuarterName = (quarter) => {
    const quarters = ['Q1 (Apr-Jun)', 'Q2 (Jul-Sep)', 'Q3 (Oct-Dec)', 'Q4 (Jan-Mar)'];
    return quarters[quarter - 1] || `Q${quarter}`;
  };

  // Generate months for dropdown
  const months = [
    { value: 'January', label: 'January' },
    { value: 'February', label: 'February' },
    { value: 'March', label: 'March' },
    { value: 'April', label: 'April' },
    { value: 'May', label: 'May' },
    { value: 'June', label: 'June' },
    { value: 'July', label: 'July' },
    { value: 'August', label: 'August' },
    { value: 'September', label: 'September' },
    { value: 'October', label: 'October' },
    { value: 'November', label: 'November' },
    { value: 'December', label: 'December' }
  ];

  // Generate quarters for dropdown
  const quarters = [
    { value: 1, label: 'Q1 (April - June)' },
    { value: 2, label: 'Q2 (July - September)' },
    { value: 3, label: 'Q3 (October - December)' },
    { value: 4, label: 'Q4 (January - March)' }
  ];

  // Generate years for dropdown (current year and previous 2 years)
  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    return [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];
  };

  // Function to decode JWT token and get company_id
  const getCompanyIdFromToken = () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        //console.error('No token found');
        return null;
      }

      // Decode JWT token (assuming it's a standard JWT)
      const payload = JSON.parse(atob(token.split('.')[1]));
      
      // Try different possible field names for company_id
      const companyId = payload.company_id || payload.companyId || payload.companyID || 
                       payload.org_id || payload.organizationId || payload.tenant_id;
      
      if (!companyId) {
        //console.error('Company ID not found in token payload:', payload);
        return null;
      }
      
      return companyId;
    } catch (error) {
      //console.error('Error decoding token:', error);
      return null;
    }
  };

  // Alternative: Get company_id from user info in localStorage
  const getCompanyIdFromUserInfo = () => {
    try {
      const userInfo = localStorage.getItem('userInfo') || sessionStorage.getItem('userInfo');
      if (userInfo) {
        const user = JSON.parse(userInfo);
        return user.company_id || user.companyId || user.organizationId;
      }
      return null;
    } catch (error) {
      //console.error('Error getting user info:', error);
      return null;
    }
  };

  useEffect(() => {
    const initializeCompanyId = () => {
      // Try multiple methods to get company_id
      let companyId = getCompanyIdFromToken();
      
      if (!companyId) {
        companyId = getCompanyIdFromUserInfo();
      }
      
      if (companyId) {
        setCompanyId(companyId);
        fetchFilingHistory(companyId);
        
        // Set default values for dropdowns
        const currentDate = new Date();
        const currentMonth = months[currentDate.getMonth()].value;
        const currentYear = currentDate.getFullYear();
        
        setPfSelectedMonth(currentMonth);
        setPfSelectedYear(currentYear);
        setPtSelectedMonth(currentMonth);
        setPtSelectedYear(currentYear);
        setEsiSelectedMonth(currentMonth);
        setEsiSelectedYear(currentYear);
        setTdsSelectedQuarter(Math.ceil((currentDate.getMonth() + 1) / 3));
        setTdsSelectedYear(currentYear);
        setForm10FromDate(getMonthStartDate(currentDate));
        setForm10ToDate(formatDateInput(currentDate));
      } else {
        //console.error('Could not retrieve company_id');
        setInitialLoading(false);
      }
    };

    initializeCompanyId();
  }, []);

  const fetchFilingHistory = async (companyId) => {
    try {
      setInitialLoading(true);
      const response = await axios.get(`${API_URL}/api/compliance/history`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
        },
      });
      setFilings(response.data.data);
    } catch (error) {
      //console.error('Failed to fetch filing history:', error);
      alert('Failed to fetch filing history: ' + (error.response?.data?.message || error.message));
    } finally {
      setInitialLoading(false);
    }
  };

  const handleFilePF = async () => {
    if (!companyId) {
      alert('Company ID not found. Please log in again.');
      return;
    }

    if (!pfSelectedMonth || !pfSelectedYear) {
      alert('Please select both month and year for PF filing.');
      return;
    }

    setPfLoading(true);
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/compliance/file/pf`, {
        
        month: pfSelectedMonth,
        year: pfSelectedYear,
      },{
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
        },
        withCredentials: true
      });
      
      setCurrentFiling(response.data.data);
      setProcessDialog({
        open: true,
        steps: response.data.data.processSteps
      });
      
      fetchFilingHistory(companyId);
    } catch (error) {
      //console.error('PF filing failed:', error);
      alert('PF filing failed: ' + (error.response?.data?.message || error.message));
    } finally {
      setPfLoading(false);
      setLoading(false);
    }
  };

  const handleGeneratePFForm10 = async () => {
    if (!companyId) {
      alert('Company ID not found. Please log in again.');
      return;
    }

    if (!form10FromDate || !form10ToDate) {
      alert('Please select leaving from and leaving to dates for PF Form 10.');
      return;
    }

    if (form10FromDate > form10ToDate) {
      alert('Leaving from date cannot be later than leaving to date.');
      return;
    }

    setForm10Loading(true);
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/compliance/file/pf-form10`, {
        
        fromDate: form10FromDate,
        toDate: form10ToDate,
      },{
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
        },
        withCredentials: true
      });

      setCurrentFiling(response.data.data);
      setProcessDialog({
        open: true,
        steps: response.data.data.processSteps
      });

      fetchFilingHistory(companyId);
    } catch (error) {
      //console.error('PF Form 10 generation failed:', error);
      alert('PF Form 10 generation failed: ' + (error.response?.data?.message || error.message));
    } finally {
      setForm10Loading(false);
      setLoading(false);
    }
  };

  const handleFilePT = async () => {
    if (!companyId) {
      alert('Company ID not found. Please log in again.');
      return;
    }

    if (!ptSelectedMonth || !ptSelectedYear) {
      alert('Please select both month and year for Professional Tax filing.');
      return;
    }

    setPtLoading(true);
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/compliance/file/pt`, {
       
        month: ptSelectedMonth,
        year: ptSelectedYear,
      },{
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
        },
        withCredentials: true
      });
      
      setCurrentFiling(response.data.data);
      setProcessDialog({
        open: true,
        steps: response.data.data.processSteps
      });
      
      fetchFilingHistory(companyId);
    } catch (error) {
      //console.error('PT filing failed:', error);
      alert('Professional Tax filing failed: ' + (error.response?.data?.message || error.message));
    } finally {
      setPtLoading(false);
      setLoading(false);
    }
  };
  
  const handleFileESI = async () => {
    if (!companyId) {
      alert('Company ID not found. Please log in again.');
      return;
    }

    if (!esiSelectedMonth || !esiSelectedYear) {
      alert('Please select both month and year for ESI filing.');
      return;
    }

    setEsiLoading(true);
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/compliance/file/esi`, {
        
        month: esiSelectedMonth,
        year: esiSelectedYear,
      },{
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
        },
        withCredentials: true
      });
      
      setCurrentFiling(response.data.data);
      setProcessDialog({
        open: true,
        steps: response.data.data.processSteps
      });
      
      fetchFilingHistory(companyId);
    } catch (error) {
      //console.error('ESI filing failed:', error);
      alert('ESI filing failed: ' + (error.response?.data?.message || error.message));
    } finally {
      setEsiLoading(false);
      setLoading(false);
    }
  };

  const handleFileTDS = async () => {
    if (!companyId) {
      alert('Company ID not found. Please log in again.');
      return;
    }

    if (!tdsSelectedQuarter || !tdsSelectedYear) {
      alert('Please select both quarter and year for TDS filing.');
      return;
    }

    setTdsLoading(true);
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/compliance/file/tds`, {
       
        quarter: tdsSelectedQuarter,
        year: tdsSelectedYear,
      },{
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
        },
        withCredentials: true
      });
      
      setCurrentFiling(response.data.data);
      setProcessDialog({
        open: true,
        steps: response.data.data.processSteps
      });
      
      fetchFilingHistory(companyId);
    } catch (error) {
      //console.error('TDS filing failed:', error);
      alert('TDS filing failed: ' + (error.response?.data?.message || error.message));
    } finally {
      setTdsLoading(false);
      setLoading(false);
    }
  };
  const handleFileLWF = async () => {
  if (!companyId) {
    alert('Company ID not found. Please log in again.');
    return;
  }

  if (!lwfSelectedHalf || !lwfSelectedYear) {
    alert('Please select both half-year and year for LWF filing.');
    return;
  }

  setLwfLoading(true);
  setLoading(true);
  try {
    const response = await axios.post(`${API_URL}/api/compliance/file/lwf`, {
     
      half_year: lwfSelectedHalf,
      year: lwfSelectedYear,
    },{
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
      },
      withCredentials: true
    });
    
    setCurrentFiling(response.data.data);
    setProcessDialog({
      open: true,
      steps: response.data.data.processSteps
    });
    
    fetchFilingHistory(companyId);
  } catch (error) {
    //console.error('LWF filing failed:', error);
    alert('LWF filing failed: ' + (error.response?.data?.message || error.message));
  } finally {
    setLwfLoading(false);
    setLoading(false);
  }
};

// Update formatPeriodDisplay function to handle LWF (around line 200)
const formatPeriodDisplay = (filing) => {
  if (isPFForm10Filing(filing)) {
    return filing.period_month;
  } else if (filing.filing_type === 'TDS') {
    const quarter = Math.ceil(filing.period_month / 3);
    return `${getQuarterName(quarter)} ${filing.period_year}`;
  } else if (filing.filing_type === 'LWF') {
    return `${filing.period_month} ${filing.period_year}`; // period_month stores "Jan-Jun" or "Jul-Dec"
  } else {
    return `${filing.period_month} ${filing.period_year}`;
  }
};

const handleDownloadFile = async (filingId) => {
  try {
    const response = await axios.get(`${API_URL}/api/compliance/download/${filingId}`, {
      responseType: 'blob',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
      }
    });
    
    // Try to get filename from custom header first
    let filename = response.headers['x-filename'];
    
    if (!filename) {
      // Fallback to Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=(["'])(.*?)\1/);
        if (match && match[2]) {
          filename = match[2];
        } else {
          const unquotedMatch = contentDisposition.match(/filename[^;=\n]*=([^;\n]*)/);
          if (unquotedMatch && unquotedMatch[1]) {
            filename = unquotedMatch[1].trim();
          }
        }
      }
    }
    
    // Final fallback
    if (!filename) {
      const filing = filings.find(f => f.filing_id === filingId);
      if (filing) {
        filename = filing.generated_file;
      } else {
        filename = `filing_${filingId}`;
      }
    }
    
    
    const blob = new Blob([response.data], { 
      type: response.headers['content-type'] || 'application/octet-stream' 
    });
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
  } catch (error) {
    //console.error('Download failed:', error);
    alert('Download failed: ' + (error.response?.data?.message || error.message));
  }
};

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED': return 'success';
      case 'PROCESSING': return 'warning';
      case 'FAILED': return 'error';
      default: return 'default';
    }
  };

  const getFilingTypeColor = (type) => {
    switch (type) {
      case 'PF': return 'primary';
      case 'PF Form 10': return 'primary';
      case 'ESI': return 'secondary';
      case 'TDS': return 'info';
      case 'PT': return 'warning';
      case 'LWF': return 'success';
      default: return 'default';
    }
  };

  // Format period display for different filing types
  // const formatPeriodDisplay = (filing) => {
  //   if (filing.filing_type === 'TDS') {
  //     const quarter = Math.ceil(filing.period_month / 3);
  //     return `${getQuarterName(quarter)} ${filing.period_year}`;
  //   } else {
  //     return `${filing.period_month} ${filing.period_year}`;
  //   }
  // };

  // Show loading while initializing
  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading compliance dashboard...
        </Typography>
      </Box>
    );
  }

  // Show error if company_id not found
  if (!companyId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Could not retrieve company information. Please log in again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{fontWeight:'bold'}}>
        Compliance Filing
      </Typography>

      {/* Filing Actions */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* PF Filing Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Description color="primary" sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                File PF
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Employee Provident Fund Monthly Return
              </Typography>
              
              {/* Month and Year Selection for PF */}
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Month</InputLabel>
                    <Select
                      value={pfSelectedMonth}
                      label="Month"
                      onChange={(e) => setPfSelectedMonth(e.target.value)}
                    >
                      {months.map((month) => (
                        <MenuItem key={month.value} value={month.value}>
                          {month.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Year</InputLabel>
                    <Select
                      value={pfSelectedYear}
                      label="Year"
                      onChange={(e) => setPfSelectedYear(e.target.value)}
                    >
                      {generateYears().map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                {pfSelectedMonth && pfSelectedYear ? 
                  `Filing for: ${pfSelectedMonth} ${pfSelectedYear}` : 
                  'Select month and year'
                }
              </Typography>

              <Button 
                variant="contained" 
                onClick={handleFilePF}
                disabled={pfLoading || !pfSelectedMonth || !pfSelectedYear}
                fullWidth
              >
                {pfLoading ? 'Processing...' : 'File PF'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* PF Form 10 Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Description color="primary" sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                PF Form 10
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Monthly PF exit report for employees leaving service
              </Typography>

              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Leaving From"
                    value={form10FromDate}
                    onChange={(e) => setForm10FromDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Leaving To"
                    value={form10ToDate}
                    onChange={(e) => setForm10ToDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                {form10FromDate && form10ToDate ?
                  `Leaving period: ${form10FromDate} to ${form10ToDate}` :
                  'Select leaving date range'
                }
              </Typography>

              <Button
                variant="contained"
                onClick={handleGeneratePFForm10}
                disabled={form10Loading || !form10FromDate || !form10ToDate}
                fullWidth
              >
                {form10Loading ? 'Generating...' : 'Generate Form 10'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* PT Filing Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Description color="warning" sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                File PT
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Professional Tax Monthly Return
              </Typography>
              
              {/* Month and Year Selection for PT */}
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Month</InputLabel>
                    <Select
                      value={ptSelectedMonth}
                      label="Month"
                      onChange={(e) => setPtSelectedMonth(e.target.value)}
                    >
                      {months.map((month) => (
                        <MenuItem key={month.value} value={month.value}>
                          {month.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Year</InputLabel>
                    <Select
                      value={ptSelectedYear}
                      label="Year"
                      onChange={(e) => setPtSelectedYear(e.target.value)}
                    >
                      {generateYears().map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                {ptSelectedMonth && ptSelectedYear ? 
                  `Filing for: ${ptSelectedMonth} ${ptSelectedYear}` : 
                  'Select month and year'
                }
              </Typography>

              <Button 
                variant="contained" 
                color="warning"
                onClick={handleFilePT}
                disabled={ptLoading || !ptSelectedMonth || !ptSelectedYear}
                fullWidth
              >
                {ptLoading ? 'Processing...' : 'File PT'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* ESI Filing Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Description color="secondary" sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                File ESI
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Employee State Insurance Contribution
              </Typography>
              
              {/* Month and Year Selection for ESI */}
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Month</InputLabel>
                    <Select
                      value={esiSelectedMonth}
                      label="Month"
                      onChange={(e) => setEsiSelectedMonth(e.target.value)}
                    >
                      {months.map((month) => (
                        <MenuItem key={month.value} value={month.value}>
                          {month.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Year</InputLabel>
                    <Select
                      value={esiSelectedYear}
                      label="Year"
                      onChange={(e) => setEsiSelectedYear(e.target.value)}
                    >
                      {generateYears().map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                {esiSelectedMonth && esiSelectedYear ? 
                  `Filing for: ${esiSelectedMonth} ${esiSelectedYear}` : 
                  'Select month and year'
                }
              </Typography>

              <Button 
                variant="contained" 
                color="secondary"
                onClick={handleFileESI}
                disabled={esiLoading || !esiSelectedMonth || !esiSelectedYear}
                fullWidth
              >
                {esiLoading ? 'Processing...' : 'File ESI'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* TDS Filing Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Description color="info" sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                File TDS
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Tax Deducted at Source Quarterly Return
              </Typography>
              
              {/* Quarter and Year Selection for TDS */}
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Quarter</InputLabel>
                    <Select
                      value={tdsSelectedQuarter}
                      label="Quarter"
                      onChange={(e) => setTdsSelectedQuarter(e.target.value)}
                    >
                      {quarters.map((quarter) => (
                        <MenuItem key={quarter.value} value={quarter.value}>
                          {quarter.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Year</InputLabel>
                    <Select
                      value={tdsSelectedYear}
                      label="Year"
                      onChange={(e) => setTdsSelectedYear(e.target.value)}
                    >
                      {generateYears().map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                {tdsSelectedQuarter && tdsSelectedYear ? 
                  `Filing for: ${getQuarterName(tdsSelectedQuarter)} ${tdsSelectedYear}` : 
                  'Select quarter and year'
                }
              </Typography>

              <Button 
                variant="contained" 
                color="info"
                onClick={handleFileTDS}
                disabled={tdsLoading || !tdsSelectedQuarter || !tdsSelectedYear}
                fullWidth
              >
                {tdsLoading ? 'Processing...' : 'File TDS'}
              </Button>
            </CardContent>
          </Card>
        </Grid>
{/* lwf */}
<Grid item xs={12} md={6}>
  <Card>
    <CardContent sx={{ textAlign: 'center' }}>
      <Description color="success" sx={{ fontSize: 48, mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        File LWF
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Labour Welfare Fund - Half-Yearly Return
      </Typography>
      
      {/* Half-Year and Year Selection for LWF */}
      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Half-Year</InputLabel>
            <Select
              value={lwfSelectedHalf}
              label="Half-Year"
              onChange={(e) => setLwfSelectedHalf(e.target.value)}
            >
              {halfYears.map((half) => (
                <MenuItem key={half.value} value={half.value}>
                  {half.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Year</InputLabel>
            <Select
              value={lwfSelectedYear}
              label="Year"
              onChange={(e) => setLwfSelectedYear(e.target.value)}
            >
              {generateYears().map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        {lwfSelectedHalf && lwfSelectedYear ? 
          `Filing for: ${lwfSelectedHalf} ${lwfSelectedYear}` : 
          'Select half-year and year'
        }
      </Typography>
      
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        Contribution: ₹20 Employee + ₹40 Employer = ₹60 per employee
      </Typography>

      <Button 
        variant="contained" 
        color="success"
        onClick={handleFileLWF}
        disabled={lwfLoading || !lwfSelectedHalf || !lwfSelectedYear}
        fullWidth
      >
        {lwfLoading ? 'Processing...' : 'File LWF'}
      </Button>
    </CardContent>
  </Card>
</Grid>

      </Grid>

      {/* Filing History */}
      <Typography variant="h5" gutterBottom>
        Filing History
      </Typography>
      
      {filings.length === 0 ? (
        <Alert severity="info">
          No filing history found. Start by filing your first compliance return.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {filings.map((filing) => (
            <Grid item xs={12} key={filing.filing_id}>
              <Card>
                <CardContent>
                  {(() => {
                    const displayType = getFilingDisplayType(filing);
                    return (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography variant="h6">
                        <Chip 
                          label={displayType}
                          color={getFilingTypeColor(displayType)}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        {filing.filing_reference}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Period: {formatPeriodDisplay(filing)} • 
                        Employees: {filing.submitted_employees} • 
                        Filed: {new Date(filing.filed_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip 
                        label={filing.status} 
                        color={getStatusColor(filing.status)}
                        size="small"
                      />
                      <Button
                        size="small"
                        startIcon={<Download />}
                        onClick={() => handleDownloadFile(filing.filing_id)}
                      >
                        Download
                      </Button>
                    </Box>
                  </Box>
                    );
                  })()}
                  
                  <Typography variant="body2">
                    File: {filing.generated_file}
                  </Typography>
                  {filing.total_contribution && filing.total_contribution > 0 && (
                    <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                      {filing.filing_type === 'TDS' ? 'Total Tax: ' : 'Total Contribution: '}
                      ₹{filing.total_contribution.toLocaleString()}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Process Dialog */}
      <Dialog 
        open={processDialog.open} 
        maxWidth="md" 
        fullWidth
        onClose={() => setProcessDialog({ open: false, steps: [] })}
      >
        <DialogTitle>
          Filing Process - {currentFiling?.filingReference}
        </DialogTitle>
        
        <DialogContent>
          <List>
            {processDialog.steps.map((step, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  {step.status === 'completed' ? (
                    <CheckCircle color="success" />
                  ) : (
                    <Schedule color="disabled" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={step.step}
                  secondary={step.timestamp && new Date(step.timestamp).toLocaleTimeString()}
                />
              </ListItem>
            ))}
          </List>
          
          {currentFiling && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body1" fontWeight="bold">
                {currentFiling.submittedEmployees} employees processed • 
                File: {currentFiling.fileGenerated}
              </Typography>
              {currentFiling.totalContribution && (
                <Typography variant="body2">
                  Total Contribution: ₹{currentFiling.totalContribution.toLocaleString()}
                </Typography>
              )}
              {currentFiling.totalTax && (
                <Typography variant="body2">
                  Total Tax: ₹{currentFiling.totalTax.toLocaleString()}
                </Typography>
              )}
              {currentFiling.summary?.incompleteRecords > 0 && (
                <Typography variant="body2" color="warning.main">
                  Incomplete Form 10 records: {currentFiling.summary.incompleteRecords}
                </Typography>
              )}
              {currentFiling.summary?.readyRecords !== undefined && (
                <Typography variant="body2">
                  Ready records: {currentFiling.summary.readyRecords}
                </Typography>
              )}
            </Alert>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={() => setProcessDialog({ open: false, steps: [] })}
          >
            Close
          </Button>
          {currentFiling && (
            <Button 
              variant="contained"
              startIcon={<Download />}
              onClick={() => handleDownloadFile(currentFiling.filingId)}
            >
              Download File
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ComplianceDashboard;
